import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, CalendarDays, LayoutGrid, AlertCircle } from 'lucide-react';
import { format, startOfMonth } from 'date-fns';
import { useBusiness } from '../../contexts/BusinessContext';
import { calculateEndAcrossBusinessHours } from '../../lib/businessHoursSchedule';
import MiniWeekView from './MiniWeekView';
import MiniMonthView from './MiniMonthView';

interface SchedulePickerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  dropOffDate?: string;
  dropOffTime?: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  serviceDuration?: number;
  showDropOff?: boolean;
  defaultView?: 'week' | 'month';
  onConfirm: (startDate: string, startTime: string, endDate: string, endTime: string, resourceId?: string, dropOffDate?: string, dropOffTime?: string, resourceIsAsset?: boolean) => void;
  disabled?: boolean;
}

export default function SchedulePickerPanel({
  isOpen,
  onClose,
  businessId,
  dropOffDate: propDropOffDate,
  dropOffTime: propDropOffTime,
  startDate: propStartDate,
  startTime: propStartTime,
  endDate: propEndDate,
  endTime: propEndTime,
  serviceDuration = 0,
  showDropOff = false,
  defaultView = 'week',
  onConfirm,
  disabled = false,
}: SchedulePickerPanelProps) {
  const { selectedBusiness } = useBusiness();

  const [viewMode, setViewMode] = useState<'week' | 'month'>(defaultView);
  const [activeField, setActiveField] = useState<'dropOff' | 'start' | 'end'>('start');
  const [tempDropOffDate, setTempDropOffDate] = useState('');
  const [tempDropOffTime, setTempDropOffTime] = useState('');
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempStartTime, setTempStartTime] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [tempEndTime, setTempEndTime] = useState('');

  const [selectedResourceId, setSelectedResourceId] = useState<string | undefined>(undefined);
  // True when the picked week-view column is an asset (assets share the grid).
  const [selectedResourceIsAsset, setSelectedResourceIsAsset] = useState(false);
  const [weekStart, setWeekStart] = useState(() => new Date());
  const [monthDate, setMonthDate] = useState(() => startOfMonth(new Date()));
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const validationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timePickerRef = useRef<HTMLDivElement>(null);

  const activeDateVal = activeField === 'dropOff' ? tempDropOffDate : activeField === 'start' ? tempStartDate : tempEndDate;
  const activeTimeVal = activeField === 'dropOff' ? tempDropOffTime : activeField === 'start' ? tempStartTime : tempEndTime;

  useEffect(() => {
    if (!isOpen) return;
    const today = new Date();

    // Only pre-fill if values were passed in (re-opening with existing data)
    setTempDropOffDate(propDropOffDate || '');
    setTempDropOffTime(propDropOffTime || '');
    setTempStartDate(propStartDate || '');
    setTempStartTime(propStartTime || '');
    setTempEndDate(propEndDate || '');
    setTempEndTime(propEndTime || '');
    setActiveField(showDropOff ? 'dropOff' : 'start');
    setViewMode(defaultView);
    setSelectedResourceId(undefined);
    setSelectedResourceIsAsset(false);
    setShowTimePicker(false);
    setValidationError(null);

    const seedDate = propStartDate ? new Date(propStartDate + 'T00:00:00') : today;
    if (!isNaN(seedDate.getTime())) {
      setWeekStart(seedDate);
      setMonthDate(startOfMonth(seedDate));
    }
  }, [isOpen]);

  useEffect(() => {
    if (showTimePicker && timePickerRef.current) {
      setTimeout(() => {
        const selected = timePickerRef.current?.querySelector('[data-selected="true"]');
        if (selected) selected.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 100);
    }
  }, [showTimePicker, activeField]);

  const getDefaultOpeningTime = (date: Date) => {
    if (!selectedBusiness?.business_hours) return '09:00';
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const dayOfWeek = days[date.getDay()];
    const daySchedule = selectedBusiness.business_hours[dayOfWeek as keyof typeof selectedBusiness.business_hours];
    if (daySchedule && daySchedule.enabled && !daySchedule.closed) return daySchedule.open;
    return '09:00';
  };

  const isWithinBusinessHours = (time: string, dateStr: string): boolean => {
    if (!selectedBusiness?.business_hours || !dateStr) return true;
    try {
      const date = new Date(dateStr + 'T00:00:00');
      if (isNaN(date.getTime())) return true;
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
      const daySchedule = selectedBusiness.business_hours[days[date.getDay()] as keyof typeof selectedBusiness.business_hours];
      if (!daySchedule || !daySchedule.enabled || daySchedule.closed) return false;
      const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
      const tm = toMin(time);
      return tm >= toMin(daySchedule.open) && tm <= toMin(daySchedule.close);
    } catch { return true; }
  };

  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? '00' : '30';
    const ampm = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return { value: `${hour.toString().padStart(2, '0')}:${minute}`, label: `${displayHour}:${minute} ${ampm}` };
  });

  const autoCalcEnd = (date: string, time: string) => {
    if (serviceDuration > 0) {
      if (selectedBusiness?.business_hours) {
        // Stretch across working days the same way the schedule editor does
        const end = calculateEndAcrossBusinessHours(selectedBusiness.business_hours, date, time, serviceDuration / 60);
        setTempEndDate(end.date);
        setTempEndTime(end.time);
      } else {
        const dt = new Date(`${date}T${time}`);
        const endDt = new Date(dt.getTime() + serviceDuration * 60000);
        setTempEndDate(format(endDt, 'yyyy-MM-dd'));
        setTempEndTime(format(endDt, 'HH:mm'));
      }
    }
  };

  const toTimestamp = (d: string, t: string) => new Date(`${d}T${t}`).getTime();

  const showValidationError = (msg: string) => {
    if (validationTimerRef.current) clearTimeout(validationTimerRef.current);
    setValidationError(msg);
    validationTimerRef.current = setTimeout(() => setValidationError(null), 4000);
  };

  const setActiveDateTime = (date: string, time: string, resourceId?: string, resourceIsAsset?: boolean) => {
    if (resourceId) {
      setSelectedResourceId(resourceId);
      setSelectedResourceIsAsset(resourceIsAsset === true);
    }
    if (activeField === 'dropOff') {
      setTempDropOffDate(date);
      setTempDropOffTime(time);
      // Auto-set start to same date/time, then auto-calc end
      setTempStartDate(date);
      setTempStartTime(time);
      autoCalcEnd(date, time);
      setValidationError(null);
    } else if (activeField === 'start') {
      setTempStartDate(date);
      setTempStartTime(time);
      autoCalcEnd(date, time);
      setValidationError(null);
    } else {
      const endTs = toTimestamp(date, time);
      const startTs = tempStartDate && tempStartTime ? toTimestamp(tempStartDate, tempStartTime) : 0;
      if (startTs && endTs <= startTs) {
        showValidationError('End date & time must be after the start date & time');
        return;
      }
      setTempEndDate(date);
      setTempEndTime(time);
      setValidationError(null);
    }
  };

  const isEndBeforeStart = (sDate: string, sTime: string, eDate: string, eTime: string) => {
    if (!sDate || !sTime || !eDate || !eTime) return false;
    return toTimestamp(eDate, eTime) <= toTimestamp(sDate, sTime);
  };

  const setActiveDate = (date: string) => {
    if (activeField === 'dropOff') {
      setTempDropOffDate(date);
      // Auto-set start date to same date
      setTempStartDate(date);
      // If drop-off time is already set, auto-calc end
      if (tempDropOffTime) {
        setTempStartTime(tempDropOffTime);
        autoCalcEnd(date, tempDropOffTime);
      }
      setValidationError(null);
    } else if (activeField === 'start') {
      setTempStartDate(date);
      if (tempStartTime) autoCalcEnd(date, tempStartTime);
      setValidationError(null);
    } else {
      setTempEndDate(date);
      const endTime = tempEndTime || tempStartTime;
      if (tempStartDate && tempStartTime && endTime && isEndBeforeStart(tempStartDate, tempStartTime, date, endTime)) {
        showValidationError('End date & time must be after the start date & time');
      } else {
        setValidationError(null);
      }
    }
  };

  const setActiveTime = (time: string) => {
    if (activeField === 'dropOff') {
      setTempDropOffTime(time);
      // Auto-set start time to same time
      setTempStartTime(time);
      // If drop-off date is already set, auto-calc end
      if (tempDropOffDate) {
        setTempStartDate(tempDropOffDate);
        autoCalcEnd(tempDropOffDate, time);
      }
      setValidationError(null);
    } else if (activeField === 'start') {
      setTempStartTime(time);
      if (tempStartDate) autoCalcEnd(tempStartDate, time);
      setValidationError(null);
    } else {
      setTempEndTime(time);
      if (tempStartDate && tempStartTime && tempEndDate && isEndBeforeStart(tempStartDate, tempStartTime, tempEndDate, time)) {
        showValidationError('End time must be after the start time');
      } else {
        setValidationError(null);
      }
    }
  };

  // Week view handlers
  const handleDayClickFromWeekView = (date: Date) => {
    const formatted = format(date, 'yyyy-MM-dd');
    setActiveDate(formatted);
  };

  const handleTimeSlotClick = (date: Date, time: string, resourceId: string, isAsset?: boolean) => {
    setActiveDateTime(format(date, 'yyyy-MM-dd'), time, resourceId, isAsset);
  };

  // Month view handler — sets date and opens time picker
  const handleMonthDayClick = (date: Date) => {
    const formatted = format(date, 'yyyy-MM-dd');
    setActiveDate(formatted);
    setShowTimePicker(true);
  };

  const handleConfirm = () => {
    if (!tempStartDate || !tempStartTime) return;
    if (tempEndDate && tempEndTime && isEndBeforeStart(tempStartDate, tempStartTime, tempEndDate, tempEndTime)) {
      showValidationError('End date & time must be after the start date & time');
      return;
    }
    onConfirm(tempStartDate, tempStartTime, tempEndDate, tempEndTime, selectedResourceId, tempDropOffDate || undefined, tempDropOffTime || undefined, selectedResourceIsAsset);
    onClose();
  };

  const formatFieldDisplay = (d: string, t: string) => {
    if (!d || !t) return null;
    try {
      return `${format(new Date(d + 'T00:00:00'), 'EEE, MMM d')} at ${format(new Date(`2000-01-01T${t}`), 'h:mm a')}`;
    } catch { return null; }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 overflow-hidden"
      style={{ zIndex: 100300 }}
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      <div className="fixed inset-0 bg-black/40" />

      <div
        className="fixed top-0 right-0 h-full w-full md:w-[600px] bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          animation: 'slideInRight 0.2s ease-out',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* Header with view toggle */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 bg-white flex-shrink-0">
          <h3 className="text-base font-semibold text-gray-900">Schedule</h3>
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => { setViewMode('week'); setShowTimePicker(false); }}
                className={`px-2.5 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                  viewMode === 'week' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <LayoutGrid className="h-3 w-3" />
                Week
              </button>
              <button
                type="button"
                onClick={() => setViewMode('month')}
                className={`px-2.5 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                  viewMode === 'month' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <CalendarDays className="h-3 w-3" />
                Month
              </button>
            </div>
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Drop Off / Start / End field selectors */}
        <div className="px-5 py-3 border-b border-gray-200 space-y-2 flex-shrink-0">
          {serviceDuration > 0 && (
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
              <Clock className="h-3 w-3" />
              <span>Service duration: <span className="font-semibold text-gray-700">
                {serviceDuration >= 60
                  ? `${Math.floor(serviceDuration / 60)}h${serviceDuration % 60 > 0 ? ` ${serviceDuration % 60}m` : ''}`
                  : `${serviceDuration}m`}
              </span></span>
            </div>
          )}

          {showDropOff && (
            <button
              type="button"
              onClick={() => { setActiveField('dropOff'); setValidationError(null); if (viewMode === 'month' && !tempDropOffDate) setShowTimePicker(false); else if (viewMode === 'month' && tempDropOffDate) setShowTimePicker(true); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
                activeField === 'dropOff'
                  ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-500'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0 ${
                activeField === 'dropOff' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                <CalendarDays className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Drop Off</div>
                <div className={`text-sm font-medium truncate ${tempDropOffDate && tempDropOffTime ? 'text-gray-900' : 'text-gray-400'}`}>
                  {formatFieldDisplay(tempDropOffDate, tempDropOffTime) || 'Select drop off date & time (optional)'}
                </div>
              </div>
              {activeField === 'dropOff' && (
                <span className="text-[10px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded flex-shrink-0">EDITING</span>
              )}
              {tempDropOffDate && tempDropOffTime && activeField !== 'dropOff' && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setTempDropOffDate(''); setTempDropOffTime(''); }}
                  className="text-gray-400 hover:text-red-500 flex-shrink-0 p-0.5"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </button>
          )}

          <button
            type="button"
            onClick={() => { setActiveField('start'); setValidationError(null); if (viewMode === 'month' && !tempStartDate) setShowTimePicker(false); else if (viewMode === 'month' && tempStartDate) setShowTimePicker(true); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
              activeField === 'start'
                ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-500'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className={`flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0 ${
              activeField === 'start' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
            }`}>
              <Clock className="h-3.5 w-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Start</div>
              <div className={`text-sm font-medium truncate ${tempStartDate && tempStartTime ? 'text-gray-900' : 'text-gray-400'}`}>
                {formatFieldDisplay(tempStartDate, tempStartTime) || 'Select start date & time'}
              </div>
            </div>
            {activeField === 'start' && (
              <span className="text-[10px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded flex-shrink-0">EDITING</span>
            )}
          </button>

          <button
            type="button"
            onClick={() => { setActiveField('end'); setValidationError(null); if (viewMode === 'month' && !tempEndDate) setShowTimePicker(false); else if (viewMode === 'month' && tempEndDate) setShowTimePicker(true); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
              validationError && activeField === 'end'
                ? 'border-red-400 bg-red-50/50 ring-1 ring-red-400'
                : activeField === 'end'
                  ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-500'
                  : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className={`flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0 ${
              validationError && activeField === 'end' ? 'bg-red-500 text-white' : activeField === 'end' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
            }`}>
              <Clock className="h-3.5 w-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">End</div>
              <div className={`text-sm font-medium truncate ${tempEndDate && tempEndTime ? 'text-gray-900' : 'text-gray-400'}`}>
                {formatFieldDisplay(tempEndDate, tempEndTime) || (tempStartDate && tempStartTime && serviceDuration > 0 ? 'Auto-calculated from duration' : 'Select end date & time')}
              </div>
            </div>
            {activeField === 'end' && (
              <span className="text-[10px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded flex-shrink-0">EDITING</span>
            )}
          </button>
        </div>

        {/* Validation error banner */}
        {validationError && (
          <div className="mx-4 mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 animate-[shake_0.3s_ease-in-out]">
            <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
            <span className="text-xs font-medium text-red-700">{validationError}</span>
            <button
              type="button"
              onClick={() => setValidationError(null)}
              className="ml-auto text-red-400 hover:text-red-600 flex-shrink-0"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Schedule preview — fills remaining space */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {viewMode === 'week' ? (
            <div className="flex-1 overflow-y-auto overflow-x-auto">
              <MiniWeekView
                businessId={businessId}
                weekStartDate={weekStart}
                onWeekChange={setWeekStart}
                onDayClick={handleDayClickFromWeekView}
                onTimeSlotClick={handleTimeSlotClick}
                selectedDate={activeDateVal}
                ghostBooking={tempStartDate && tempStartTime && tempEndDate && tempEndTime ? {
                  date: tempStartDate,
                  time: tempStartTime,
                  durationMinutes: Math.max(
                    Math.round((toTimestamp(tempEndDate, tempEndTime) - toTimestamp(tempStartDate, tempStartTime)) / 60000),
                    30
                  ),
                  resourceId: selectedResourceId,
                } : null}
              />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {/* Month grid — prevent flex-1 from stretching inside scroll container */}
              <div style={{ flex: 'none' }}>
                <MiniMonthView
                  businessId={businessId}
                  currentMonth={monthDate}
                  onMonthChange={setMonthDate}
                  onDayClick={handleMonthDayClick}
                  selectedDate={activeDateVal}
                  ghostBooking={tempStartDate && tempStartTime && tempEndDate && tempEndTime ? {
                    startDate: tempStartDate,
                    startTime: tempStartTime,
                    endDate: tempEndDate,
                    endTime: tempEndTime,
                  } : null}
                />
              </div>

              {/* Time picker — inline below month, same scroll */}
              {showTimePicker && (
                <div className="border-t border-gray-200">
                  <div className="px-4 py-2 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Select Time
                      {activeDateVal && (
                        <span className="ml-2 text-blue-600 normal-case font-medium">
                          {format(new Date(activeDateVal + 'T00:00:00'), 'EEE, MMM d')}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowTimePicker(false)}
                      className="text-[10px] font-medium text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      Hide
                    </button>
                  </div>
                  <div ref={timePickerRef} className="px-4 pb-3">
                    <div className="grid grid-cols-4 gap-1">
                      {timeOptions.map((time) => {
                        const isSelected = time.value === activeTimeVal;
                        const withinHours = activeDateVal ? isWithinBusinessHours(time.value, activeDateVal) : true;
                        return (
                          <button
                            type="button"
                            key={time.value}
                            data-selected={isSelected}
                            onClick={() => setActiveTime(time.value)}
                            className={`
                              px-3 py-2 text-xs rounded-lg transition-colors font-medium border
                              ${isSelected
                                ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600'
                                : withinHours
                                  ? 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                                  : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                              }
                            `}
                            style={{ minHeight: '36px' }}
                          >
                            {time.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-between bg-white flex-shrink-0">
          <div className="text-xs text-gray-500 leading-tight">
            {tempStartDate && tempStartTime ? (
              <div>
                {showDropOff && tempDropOffDate && tempDropOffTime && (
                  <div className="mb-0.5">
                    <span className="text-gray-400">Drop off: </span>
                    <span className="font-medium text-gray-700">
                      {format(new Date(tempDropOffDate + 'T00:00:00'), 'MMM d')} {format(new Date(`2000-01-01T${tempDropOffTime}`), 'h:mm a')}
                    </span>
                  </div>
                )}
                <span className="font-medium text-gray-700">
                  {format(new Date(tempStartDate + 'T00:00:00'), 'MMM d')} {format(new Date(`2000-01-01T${tempStartTime}`), 'h:mm a')}
                </span>
                {tempEndDate && tempEndTime && (
                  <>
                    {' → '}
                    <span className="font-medium text-gray-700">
                      {format(new Date(tempEndDate + 'T00:00:00'), 'MMM d')} {format(new Date(`2000-01-01T${tempEndTime}`), 'h:mm a')}
                    </span>
                  </>
                )}
              </div>
            ) : 'Pick a start time'}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!tempStartDate || !tempStartTime || disabled || (!!tempEndDate && !!tempEndTime && isEndBeforeStart(tempStartDate, tempStartTime, tempEndDate, tempEndTime))}
              className="px-4 py-1.5 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(5px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(2px); }
        }
      `}</style>
    </div>,
    document.body
  );
}
